import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  User,
  Car,
  KeyRound,
  UserCheck,
  Clock,
  ExternalLink,
  CheckCircle2,
  LogOut,
  UserPlus,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  FormField,
  TextInput,
  SelectDropdown,
  RadioGroup,
  Button,
  StatusBadge,
  Modal,
  EmptyState,
  LoadingSkeleton,
} from "../../../components/ui";
import { getStoredAuthUser } from "../../../services/authService";
import { getSocieties, getTowersBySociety } from "../../../services/societyService";
import { getFlatsBySociety } from "../../../services/flatsService";
import {
  checkInVisitorEntry,
  checkOutVisitorEntry,
  createVisitorEntry,
  getGuardVisitorEntries,
  getVisitorTypes,
  resolveResidentForUnit,
} from "../../../services/visitorService";
import {
  searchPreApprovedInvite,
  checkInPreApprovedInvite,
  checkOutPreApprovedInvite,
  denyPreApprovedInvite,
  getExpectedInvites,
} from "../../../services/inviteService";
import { visitorEntrySchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import styles from "./GuardVisitors.module.css";

const TAB_OPTIONS = [
  { value: "adhoc", label: "Manual Walk-in", icon: <User size={14} /> },
  { value: "vehicle_draft", label: "Parking Draft", icon: <Car size={14} /> },
  { value: "passcode_verify", label: "Verify Pass", icon: <KeyRound size={14} /> },
  { value: "preapproved", label: "Guest Invite", icon: <UserCheck size={14} /> },
];

const GATE_OPTIONS = [
  { value: "1", label: "Gate 1: Main Pedestrian Gate" },
  { value: "2", label: "Gate 2: Parking & Cargo Gate" },
];

export default function GuardVisitors() {
  const authUser = getStoredAuthUser();
  const [societies, setSocieties] = useState([]);
  const [societyId, setSocietyId] = useState(() => {
    const fromUser = Number(authUser?.society_id);
    if (Number.isFinite(fromUser) && fromUser > 0) return String(fromUser);
    const cached = Number(localStorage.getItem("visitor:test-society-id"));
    return Number.isFinite(cached) && cached > 0 ? String(cached) : "114";
  });
  const guardId = Number(authUser?.user_id) || 1;

  // Gate Duty state (Gate 1 Pedestrian vs Gate 2 Parking)
  const [gateId, setGateId] = useState(() => {
    return localStorage.getItem("guard:gate-id") || (authUser?.user_id === 98 ? "2" : "1");
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);

  // Tabs: "adhoc" | "vehicle_draft" | "passcode_verify" | "preapproved"
  const [activeTab, setActiveTab] = useState("adhoc");

  // Pre-approved state
  const [passcode, setPasscode] = useState("");
  const [searchingInvite, setSearchingInvite] = useState(false);
  const [searchedInvite, setSearchedInvite] = useState(null);
  const [inviteActionLoading, setInviteActionLoading] = useState(false);
  const [, setExpectedInvites] = useState([]);

  // Vehicle Draft State (Flow B)
  const [draftVehicleNumber, setDraftVehicleNumber] = useState("");
  const [draftVisitorName, setDraftVisitorName] = useState("");
  const [draftDriverPhone, setDraftDriverPhone] = useState("");
  const [createdDraft, setCreatedDraft] = useState(null);
  const [activeDrafts, setActiveDrafts] = useState([]);
  const [draftTimerSec, setDraftTimerSec] = useState(300);

  // Pass Code Lookup / Check-in State
  const [verifyPassCode, setVerifyPassCode] = useState("");
  const [verifiedPassData, setVerifiedPassData] = useState(null);
  const [verifyingPass, setVerifyingPass] = useState(false);

  // Department Head / Receptionist Host Assignment Modal
  const [assignModalEntry, setAssignModalEntry] = useState(null);
  const [assignedUserId, setAssignedUserId] = useState("");
  const [eligibleStaff, setEligibleStaff] = useState([]);

  // End of Day confirmation modal
  const [showEodModal, setShowEodModal] = useState(false);
  const [eodLoading, setEodLoading] = useState(false);

  const [towers, setTowers] = useState([]);
  const [flats, setFlats] = useState([]);
  const [, setVisitorTypes] = useState([]);
  const [entries, setEntries] = useState([]);

  const [towerId, setTowerId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [resolvedResident, setResolvedResident] = useState(null);

  const [form, setForm] = useState({
    visitor_name: "",
    visitor_phone: "",
    visitor_type_id: "",
    purpose: "",
    vehicle_number: "",
    no_of_visitors: 1,
    remarks: "",
  });

  const flatsForTower = useMemo(() => {
    const nextTowerId = Number(towerId);
    if (!nextTowerId) return [];
    const towerFlats = (flats || []).filter(
      (f) => Number(f.tower_id) === nextTowerId
    );

    const mergedFlatNumbers = new Set(
      towerFlats
        .map((f) => String(f.flat_number || ""))
        .filter((n) => n.includes("+"))
    );

    return towerFlats.filter((f) => {
      const flatNumber = String(f.flat_number || "");
      const unitType = String(f.unit_type || "");

      if (unitType.toUpperCase() === "MERGED") return false;
      if (flatNumber.startsWith("M-")) return false;
      if (flatNumber.includes("+")) return true;
      if (mergedFlatNumbers.has(String(f.merged_from || ""))) return false;
      return true;
    });
  }, [flats, towerId]);

  const loadDrafts = useCallback(async () => {
    if (!societyId) return;
    try {
      const res = await fetch(`/api/public/visitor/guard/drafts?society_id=${societyId}`);
      const data = await res.json();
      if (data.drafts) setActiveDrafts(data.drafts);
    } catch (e) {
      console.error("Load drafts error:", e);
    }
  }, [societyId]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const societyData = await getSocieties();
      const activeSocieties = societyData || [];
      setSocieties(activeSocieties);

      const nextSocietyId =
        Number(societyId) ||
        Number(authUser?.society_id) ||
        Number(activeSocieties?.[0]?.society_id) ||
        "114";

      if (nextSocietyId && String(nextSocietyId) !== String(societyId || "")) {
        setSocietyId(String(nextSocietyId));
      }
      if (nextSocietyId) {
        localStorage.setItem("visitor:test-society-id", String(nextSocietyId));
      }

      const [towerData, flatData, typeData, entryData, expectedInvitesData] = nextSocietyId
        ? await Promise.all([
            getTowersBySociety(nextSocietyId),
            getFlatsBySociety(nextSocietyId),
            getVisitorTypes(),
            getGuardVisitorEntries({ societyId: nextSocietyId }),
            getExpectedInvites(nextSocietyId),
          ])
        : [[], [], await getVisitorTypes(), [], { invites: [] }];

      setTowers(towerData || []);
      setFlats(flatData || []);
      setVisitorTypes(typeData || []);
      setEntries(entryData || []);
      setExpectedInvites(expectedInvitesData?.invites || []);

      if ((towerData || []).length) {
        setTowerId(String(towerData[0].tower_id));
      }

      await loadDrafts();
    } catch (error) {
      console.error("LOAD GUARD VISITOR PAGE ERROR:", error);
      toast.error("Unable to load visitors");
    } finally {
      setLoading(false);
    }
  }, [authUser?.society_id, societyId, loadDrafts]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Draft countdown interval
  useEffect(() => {
    if (activeDrafts.length === 0 && !createdDraft) return;
    const interval = setInterval(() => {
      setDraftTimerSec((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeDrafts, createdDraft]);

  useEffect(() => {
    setUnitId("");
    setResolvedResident(null);
  }, [towerId]);

  useEffect(() => {
    const run = async () => {
      const nextUnitId = Number(unitId);
      if (!nextUnitId) {
        setResolvedResident(null);
        return;
      }
      try {
        const data = await resolveResidentForUnit(nextUnitId);
        setResolvedResident(data);
      } catch (error) {
        console.error("RESOLVE RESIDENT ERROR:", error);
        setResolvedResident(null);
      }
    };
    run();
  }, [unitId]);

  const updateForm = (key, value) =>
    setForm((prev) => {
      if (key === "visitor_phone") {
        const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
        return { ...prev, visitor_phone: digits };
      }
      return { ...prev, [key]: value };
    });

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const parsed = visitorEntrySchema.safeParse({
      ...form,
      society_id: societyId,
      tower_id: towerId,
      unit_id: unitId,
      resident_user_id: resolvedResident?.resident_user_id || null,
      user_id: guardId,
    });
    if (!parsed.success) return toast.error(zodErrorToFirstMessage(parsed.error));

    setSubmitting(true);
    try {
      await createVisitorEntry(parsed.data);
      toast.success("Visitor request created successfully");
      setForm({
        visitor_name: "",
        visitor_phone: "",
        visitor_type_id: "",
        purpose: "",
        vehicle_number: "",
        no_of_visitors: 1,
        remarks: "",
      });
      await loadPage();
    } catch (error) {
      console.error("CREATE VISITOR ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to create request");
    } finally {
      setSubmitting(false);
    }
  };

  // Flow B: Guard creates vehicle draft
  const handleCreateVehicleDraft = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!draftVehicleNumber.trim()) return toast.warn("Vehicle number is required");
    if (!draftVisitorName.trim()) return toast.warn("Visitor name is required");

    setSubmitting(true);
    try {
      const res = await fetch("/api/public/visitor/guard/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          society_id: societyId,
          vehicle_number: draftVehicleNumber.trim().toUpperCase(),
          visitor_name: draftVisitorName.trim(),
          driver_phone: draftDriverPhone.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCreatedDraft(data);
        setDraftTimerSec(300);
        toast.success("Draft created! Show QR code to driver/visitor.");
        setDraftVehicleNumber("");
        setDraftVisitorName("");
        setDraftDriverPhone("");
        await loadDrafts();
      } else {
        toast.error(data.error || "Failed to create draft");
      }
    } catch (err) {
      toast.error("Network error creating draft");
    } finally {
      setSubmitting(false);
    }
  };

  // Flow A/B: Look up visitor by pass code
  const handleLookupPassCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!verifyPassCode.trim()) return toast.warn("Please enter a pass code");
    setVerifyingPass(true);
    setVerifiedPassData(null);
    try {
      const res = await fetch(`/api/public/visitor/status/${encodeURIComponent(verifyPassCode.trim())}`);
      const data = await res.json();
      if (res.ok && data.success) {
        setVerifiedPassData(data);
        toast.success("Visitor pass loaded!");
      } else {
        toast.error(data.error || "Pass not found or invalid");
      }
    } catch (err) {
      toast.error("Network error checking pass");
    } finally {
      setVerifyingPass(false);
    }
  };

  // Pass Check-in
  const handlePassCheckIn = async (passCode) => {
    setActionId(passCode);
    try {
      const res = await fetch("/api/public/visitor/entries/0/check-in", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pass_code: passCode }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Visitor verified & checked in successfully!");
        setVerifiedPassData(null);
        setVerifyPassCode("");
        await loadPage();
      } else {
        toast.error(data.error || "Check-in failed");
      }
    } catch (err) {
      toast.error("Error during check-in");
    } finally {
      setActionId(null);
    }
  };

  const handleCheckIn = async (id) => {
    setActionId(id);
    try {
      await checkInVisitorEntry(id);
      toast.success("Checked in successfully");
      await loadPage();
    } catch (error) {
      console.error("CHECK IN ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to check in");
    } finally {
      setActionId(null);
    }
  };

  const handleCheckOut = async (id) => {
    setActionId(id);
    try {
      await checkOutVisitorEntry(id);
      toast.success("Checked out successfully");
      await loadPage();
    } catch (error) {
      console.error("CHECK OUT ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to check out");
    } finally {
      setActionId(null);
    }
  };

  // Auto Check-out for End of Day
  const handleAutoCheckoutEOD = async () => {
    setEodLoading(true);
    try {
      const res = await fetch("/api/public/visitor/entries/auto-checkout-eod", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ society_id: societyId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Auto-checked out ${data.auto_checked_out_count} visitors!`);
        setShowEodModal(false);
        await loadPage();
      } else {
        toast.error(data.error || "Auto checkout failed");
      }
    } catch (e) {
      toast.error("Auto checkout error");
    } finally {
      setEodLoading(false);
    }
  };

  const handleInviteSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!passcode.trim()) {
      return toast.warn("Please enter an invite code");
    }
    setSearchingInvite(true);
    setSearchedInvite(null);
    try {
      const invite = await searchPreApprovedInvite(passcode.trim(), societyId);
      setSearchedInvite(invite);
      toast.success("Invite found!");
    } catch (error) {
      console.error("SEARCH INVITE ERROR:", error);
      toast.error(error?.response?.data?.error || "Invite not found or invalid");
    } finally {
      setSearchingInvite(false);
    }
  };

  const handleInviteCheckIn = async (id) => {
    setInviteActionLoading(true);
    try {
      await checkInPreApprovedInvite(id);
      toast.success("Guest checked in successfully");
      const invite = await searchPreApprovedInvite(passcode.trim(), societyId);
      setSearchedInvite(invite);
      await loadPage();
    } catch (error) {
      console.error("INVITE CHECK IN ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to check in guest");
    } finally {
      setInviteActionLoading(false);
    }
  };

  const handleInviteCheckOut = async (id) => {
    setInviteActionLoading(true);
    try {
      await checkOutPreApprovedInvite(id);
      toast.success("Guest checked out successfully");
      const invite = await searchPreApprovedInvite(passcode.trim(), societyId);
      setSearchedInvite(invite);
      await loadPage();
    } catch (error) {
      console.error("INVITE CHECK OUT ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to check out guest");
    } finally {
      setInviteActionLoading(false);
    }
  };

  const handleInviteDeny = async (id) => {
    setInviteActionLoading(true);
    try {
      await denyPreApprovedInvite(id);
      toast.success("Guest entry denied");
      const invite = await searchPreApprovedInvite(passcode.trim(), societyId);
      setSearchedInvite(invite);
      await loadPage();
    } catch (error) {
      console.error("INVITE DENY ERROR:", error);
      toast.error(error?.response?.data?.error || "Unable to deny entry");
    } finally {
      setInviteActionLoading(false);
    }
  };

  // Open host assignment modal for unassigned visitors
  const handleOpenAssignModal = async (entry) => {
    setAssignModalEntry(entry);
    setAssignedUserId("");
    try {
      const deptParam = entry.department_id || entry.unit_id;
      const url = deptParam
        ? `/api/public/visitor/hosts?society_id=${societyId}&department_id=${deptParam}&otp_token=admin`
        : `/api/public/visitor/hosts?society_id=${societyId}&otp_token=admin`;
      const res = await fetch(url, {
        headers: { "x-otp-token": "admin" },
      });
      const data = await res.json();
      if (data.hosts) setEligibleStaff(data.hosts);
    } catch (e) {
      console.error("Staff fetch error:", e);
    }
  };

  const handleAssignHostSubmit = async () => {
    if (!assignedUserId || !assignModalEntry) return toast.warn("Select a staff member");
    try {
      const res = await fetch(`/api/public/visitor/entries/${assignModalEntry.id}/assign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigned_user_id: assignedUserId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Host assigned!");
        setAssignModalEntry(null);
        await loadPage();
      } else {
        toast.error(data.error || "Failed to assign host");
      }
    } catch (err) {
      toast.error("Error assigning host");
    }
  };

  const currentSocietyName = useMemo(() => {
    const matched = societies.find((s) => String(s.society_id) === String(societyId));
    return matched ? matched.society_name : "AsmitA Operations";
  }, [societies, societyId]);

  return (
    <AdminLayout title="Gate Operations & Visitor Intake">
      <div className={styles.page}>
        {/* Top Header & Property Gate Bar */}
        <div className={styles.headerRow}>
          <div className={styles.titleArea}>
            <h1 className={styles.title}>{currentSocietyName} — Gate Terminal</h1>
            <p className={styles.subTitle}>
              Real-time pedestrian, vehicle & delivery gate access control, pass code verification, and dispatch logs.
            </p>
          </div>

          <div className={styles.controls}>
            <div style={{ minWidth: "240px" }}>
              <SelectDropdown
                name="gateSelect"
                value={gateId}
                onChange={(e) => {
                  setGateId(e.target.value);
                  localStorage.setItem("guard:gate-id", e.target.value);
                  toast.info(`Switched duty to Gate ${e.target.value}`);
                }}
                options={GATE_OPTIONS}
              />
            </div>

            <div style={{ minWidth: "200px" }}>
              <SelectDropdown
                name="societySelect"
                value={societyId}
                onChange={(e) => setSocietyId(e.target.value)}
                disabled={Boolean(authUser?.society_id)}
                options={[
                  { label: "Select property...", value: "" },
                  ...(societies || []).map((s) => ({
                    label: s.society_name,
                    value: String(s.society_id),
                  })),
                ]}
              />
            </div>

            <div className={styles.guardBadge}>
              <ShieldCheck size={14} />
              <span>Guard: {authUser?.full_name || authUser?.email_id || `ID #${guardId}`}</span>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSkeleton count={4} height="80px" />
        ) : (
          <div className={styles.grid}>
            {/* Left Column: Interactive Gate Controls */}
            <div className={styles.card}>
              <div style={{ marginBottom: "16px" }}>
                <RadioGroup
                  name="activeTab"
                  value={activeTab}
                  onChange={(e) => {
                    setActiveTab(e.target.value);
                    if (e.target.value === "vehicle_draft") loadDrafts();
                  }}
                  options={TAB_OPTIONS}
                  variant="segmented"
                />
              </div>

              {/* TAB 1: Ad-hoc Manual Walk-in Entry */}
              {activeTab === "adhoc" && (
                <div>
                  <h2 className={styles.cardTitle}>Manual Visitor Intake (Fallback)</h2>
                  <p className={styles.subTitle} style={{ marginBottom: "16px" }}>
                    Record walk-in guests when self-service kiosk or QR passes are unavailable.
                  </p>

                  <form onSubmit={handleSubmit} className={styles.formGrid}>
                    <div className={styles.formGrid2}>
                      <FormField label="Tower / Wing" required>
                        <SelectDropdown
                          name="towerId"
                          value={towerId}
                          onChange={(e) => setTowerId(e.target.value)}
                          options={(towers || []).map((t) => ({
                            label: t.tower_name,
                            value: String(t.tower_id),
                          }))}
                          placeholder="Select tower..."
                        />
                      </FormField>

                      <FormField label="Destination Unit" required>
                        <SelectDropdown
                          name="unitId"
                          value={unitId}
                          onChange={(e) => setUnitId(e.target.value)}
                          options={[
                            { label: "Select unit...", value: "" },
                            ...flatsForTower.map((f) => ({
                              label: f.flat_number,
                              value: String(f.flat_id),
                            })),
                          ]}
                          placeholder="Select department/flat..."
                        />
                      </FormField>
                    </div>

                    <div className={styles.formGrid2}>
                      <FormField label="Visitor Full Name" required>
                        <TextInput
                          name="visitor_name"
                          value={form.visitor_name}
                          onChange={(e) => updateForm("visitor_name", e.target.value)}
                          placeholder="e.g. Rahul Sharma"
                          leadIcon={<User size={15} />}
                          required
                        />
                      </FormField>

                      <FormField label="Mobile Number" required>
                        <TextInput
                          name="visitor_phone"
                          value={form.visitor_phone}
                          onChange={(e) => updateForm("visitor_phone", e.target.value)}
                          placeholder="10-digit phone"
                          prefixText="+91"
                          maxLength={10}
                          required
                        />
                      </FormField>
                    </div>

                    <div className={styles.formGrid2}>
                      <FormField label="Vehicle Number" optionalLabel>
                        <TextInput
                          name="vehicle_number"
                          value={form.vehicle_number}
                          onChange={(e) => updateForm("vehicle_number", e.target.value.toUpperCase())}
                          placeholder="e.g. MH 02 AB 1234"
                          leadIcon={<Car size={15} />}
                        />
                      </FormField>

                      <FormField label="Purpose of Visit" optionalLabel>
                        <TextInput
                          name="purpose"
                          value={form.purpose}
                          onChange={(e) => updateForm("purpose", e.target.value)}
                          placeholder="e.g. Business Meeting, Maintenance"
                        />
                      </FormField>
                    </div>

                    <div className={styles.submitSlot}>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={submitting}
                        fullWidth
                      >
                        Submit Manual Visitor Entry
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* TAB 2: Flow B Parking Gate Draft */}
              {activeTab === "vehicle_draft" && (
                <div>
                  <h2 className={styles.cardTitle}>Create Vehicle Draft (Parking Gate)</h2>
                  <p className={styles.subTitle} style={{ marginBottom: "16px" }}>
                    Generate a high-speed vehicle pass draft. A dynamic QR code with a 5-minute timer will be generated for the driver to scan.
                  </p>

                  <form onSubmit={handleCreateVehicleDraft} className={styles.formGrid}>
                    <FormField label="Vehicle Plate Number" required>
                      <TextInput
                        name="draftVehicleNumber"
                        placeholder="e.g. MH 04 AB 1234"
                        value={draftVehicleNumber}
                        onChange={(e) => setDraftVehicleNumber(e.target.value.toUpperCase())}
                        leadIcon={<Car size={15} />}
                        required
                      />
                    </FormField>

                    <FormField label="Driver / Visitor Name" required>
                      <TextInput
                        name="draftVisitorName"
                        placeholder="Full name of driver or primary occupant"
                        value={draftVisitorName}
                        onChange={(e) => setDraftVisitorName(e.target.value)}
                        leadIcon={<User size={15} />}
                        required
                      />
                    </FormField>

                    <FormField label="Driver Contact Phone" optionalLabel helperText="Used for automated SMS pass updates">
                      <TextInput
                        name="draftDriverPhone"
                        placeholder="10-digit mobile number"
                        prefixText="+91"
                        value={draftDriverPhone}
                        onChange={(e) => setDraftDriverPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        maxLength={10}
                      />
                    </FormField>

                    <div className={styles.submitSlot}>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={submitting}
                        fullWidth
                      >
                        Generate Vehicle Draft & Dynamic QR
                      </Button>
                    </div>
                  </form>

                  {/* Display newly created draft */}
                  {createdDraft && (
                    <div className={styles.qrDraftBox}>
                      <div className={styles.qrDraftTitle}>
                        <Car size={16} /> Show QR Code to Driver
                      </div>
                      <div className={styles.qrDraftCode}>{createdDraft.draft_code}</div>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
                          window.location.origin + createdDraft.qr_url
                        )}`}
                        alt="Draft QR code"
                        className={styles.qrImage}
                      />
                      <div className={styles.qrCountdown}>
                        <Clock size={15} /> Timer: {Math.floor(draftTimerSec / 60)}:
                        {String(draftTimerSec % 60).padStart(2, "0")} remaining
                      </div>
                      <div className={styles.qrNotice}>
                        Driver scans with phone camera to complete destination & live photo.
                      </div>
                    </div>
                  )}

                  {/* Active Open Drafts List */}
                  <div style={{ marginTop: "24px" }}>
                    <h3 className={styles.cardTitle} style={{ fontSize: "1rem" }}>
                      Active Open Drafts ({activeDrafts.length})
                    </h3>
                    {activeDrafts.length === 0 ? (
                      <EmptyState
                        title="No Open Drafts"
                        description="All vehicle drafts have been completed or expired."
                      />
                    ) : (
                      <div style={{ marginTop: "12px" }}>
                        {activeDrafts.map((d) => (
                          <div key={d.id} className={styles.draftItemCard}>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a" }}>
                                {d.vehicle_number} • {d.visitor_name}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "2px" }}>
                                Code: <strong>{d.draft_code}</strong> • Time Left: {d.seconds_remaining}s
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<ExternalLink size={13} />}
                              onClick={() => window.open(d.qr_url, "_blank")}
                            >
                              Open Kiosk
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 3: Verify Pass Code */}
              {activeTab === "passcode_verify" && (
                <div>
                  <h2 className={styles.cardTitle}>Verify Pass Code & Live Photo</h2>
                  <p className={styles.subTitle} style={{ marginBottom: "16px" }}>
                    Enter or scan the visitor&apos;s digital gate pass code (e.g. ASM-3674) to inspect their live selfie and approval state.
                  </p>

                  <form onSubmit={handleLookupPassCode} style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        name="verifyPassCode"
                        placeholder="e.g. ASM-3674"
                        value={verifyPassCode}
                        onChange={(e) => setVerifyPassCode(e.target.value.toUpperCase())}
                        leadIcon={<KeyRound size={15} />}
                        required
                      />
                    </div>
                    <Button type="submit" variant="primary" loading={verifyingPass}>
                      Lookup Pass
                    </Button>
                  </form>

                  {/* Verified Pass Card */}
                  {verifiedPassData && (
                    <div className={styles.verifiedCard}>
                      <div className={styles.verifiedHeader}>
                        {verifiedPassData.photo_url ? (
                          <img
                            src={verifiedPassData.photo_url}
                            alt="Visitor Selfie"
                            className={styles.verifiedAvatar}
                          />
                        ) : (
                          <div className={styles.verifiedAvatarPlaceholder}>No Photo</div>
                        )}
                        <div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 900, color: "#0f172a" }}>
                            {verifiedPassData.visitor_name}
                          </div>
                          {verifiedPassData.vehicle_number && (
                            <div className={styles.vehicleTag} style={{ marginTop: "4px" }}>
                              <Car size={13} /> {verifiedPassData.vehicle_number}
                            </div>
                          )}
                          <div style={{ marginTop: "6px" }}>
                            <StatusBadge status={verifiedPassData.status} />
                          </div>
                        </div>
                      </div>

                      <div className={styles.verifiedDetailsGrid}>
                        <div><strong>Floor:</strong> Floor {verifiedPassData.floor_number || "1"}</div>
                        <div><strong>Department:</strong> {verifiedPassData.department_name || "-"}</div>
                        <div><strong>Host:</strong> {verifiedPassData.host_name || "Unassigned"}</div>
                        <div><strong>Cabin:</strong> {verifiedPassData.cabin_code || "Open Floor"}</div>
                        <div><strong>Desk:</strong> {verifiedPassData.desk_code || "Floor Area"}</div>
                        <div><strong>Pass Code:</strong> {verifiedPassData.pass_code}</div>
                      </div>

                      <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                          variant="primary"
                          icon={<CheckCircle2 size={16} />}
                          onClick={() => handlePassCheckIn(verifiedPassData.pass_code)}
                          disabled={actionId === verifiedPassData.pass_code || verifiedPassData.status !== "APPROVED"}
                        >
                          Verify & Grant Check-In
                        </Button>
                        <Button
                          variant="outline"
                          icon={<LogOut size={16} />}
                          onClick={() => handleCheckOut(verifiedPassData.id)}
                          disabled={actionId === verifiedPassData.id || verifiedPassData.status !== "CHECKED_IN"}
                        >
                          Check Out
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 4: Pre-Approved Invite Search */}
              {activeTab === "preapproved" && (
                <div>
                  <h2 className={styles.cardTitle}>Search Pre-Approved Invite</h2>
                  <p className={styles.subTitle} style={{ marginBottom: "16px" }}>
                    Lookup a resident or employee guest invitation pass code before allowing entry.
                  </p>

                  <form onSubmit={handleInviteSearch} style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        name="passcode"
                        placeholder="Enter 6-digit Invite Code"
                        value={passcode}
                        onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                        leadIcon={<UserCheck size={15} />}
                        required
                      />
                    </div>
                    <Button type="submit" variant="primary" loading={searchingInvite}>
                      Search Invite
                    </Button>
                  </form>

                  {searchedInvite && (
                    <div className={styles.verifiedCard} style={{ borderColor: "#6366f1", background: "#f8fafc" }}>
                      <div className={styles.verifiedDetailsGrid}>
                        <div><strong>Guest Name:</strong> {searchedInvite.visitor_name || "-"}</div>
                        <div><strong>Phone:</strong> {searchedInvite.guest_phone || "-"}</div>
                        <div><strong>Destination Unit:</strong> {searchedInvite.flat_number || "-"}</div>
                        <div><strong>Host:</strong> {searchedInvite.resident_name || "-"}</div>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <strong>Status:</strong> <StatusBadge status={searchedInvite.status} />
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<CheckCircle2 size={14} />}
                          onClick={() => handleInviteCheckIn(searchedInvite.id)}
                          disabled={inviteActionLoading || searchedInvite.status !== "APPROVED"}
                        >
                          Check In Guest
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<LogOut size={14} />}
                          onClick={() => handleInviteCheckOut(searchedInvite.id)}
                          disabled={inviteActionLoading || searchedInvite.status !== "CHECKED_IN"}
                        >
                          Check Out
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleInviteDeny(searchedInvite.id)}
                          disabled={inviteActionLoading || !["APPROVED", "PENDING"].includes(searchedInvite.status)}
                        >
                          Deny Entry
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column: Live Entries & Reception Waiting List */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <h2 className={styles.cardTitle}>Live Visitor Activity ({entries.length})</h2>
                  <p className={styles.subTitle}>Current operational intake and dispatch logs</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<LogOut size={14} />}
                  onClick={() => setShowEodModal(true)}
                >
                  End of Day Auto Check-out
                </Button>
              </div>

              {entries.length === 0 ? (
                <EmptyState
                  title="No Live Entries Today"
                  description="When visitors register at the kiosk or pass through the gate, their logs will appear here."
                />
              ) : (
                <div className={styles.entriesList}>
                  {entries.map((row) => (
                    <div key={row.id} className={styles.entryRow}>
                      <div className={styles.entryInfoSide}>
                        {row.photo_url ? (
                          <img
                            src={row.photo_url}
                            alt={row.visitor_name}
                            className={styles.entryAvatar}
                          />
                        ) : (
                          <div className={styles.verifiedAvatarPlaceholder} style={{ width: "48px", height: "48px" }}>
                            <User size={18} />
                          </div>
                        )}

                        <div className={styles.entryDetails}>
                          <div className={styles.entryTitleRow}>
                            <span className={styles.entryName}>{row.visitor_name}</span>
                            <span className={styles.entryPhone}>{row.visitor_phone}</span>
                            {row.vehicle_number && (
                              <span className={styles.vehicleTag}>
                                <Car size={12} /> {row.vehicle_number}
                              </span>
                            )}
                            {row.pass_code && (
                              <span className={styles.passTag}>Pass: {row.pass_code}</span>
                            )}
                          </div>

                          <div className={styles.entryMetaText}>
                            Unit: <strong>{row.unit_number || "-"}</strong> • Host:{" "}
                            <strong>{row.resident_name || "Unassigned"}</strong>
                            {row.cabin_code ? ` • Cabin: ${row.cabin_code}` : ""}
                            {row.desk_code ? ` • Desk: ${row.desk_code}` : ""}
                          </div>

                          <div className={styles.entryMetaText} style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                            Requested: {row.requested_at ? new Date(row.requested_at).toLocaleTimeString() : "-"}
                            {row.purpose ? ` • ${row.purpose}` : ""}
                          </div>
                        </div>
                      </div>

                      <div className={styles.entryActions}>
                        <StatusBadge status={row.status} />

                        {/* Unassigned Action for Dept Head / Receptionist */}
                        {(row.status === "UNASSIGNED" || (!row.resident_name && row.status === "PENDING")) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<UserPlus size={14} />}
                            onClick={() => handleOpenAssignModal(row)}
                          >
                            Assign Host
                          </Button>
                        )}

                        <Button
                          variant="primary"
                          size="sm"
                          icon={<CheckCircle2 size={14} />}
                          onClick={() => handleCheckIn(row.id)}
                          disabled={actionId === row.id || row.status !== "APPROVED"}
                        >
                          Check In
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          icon={<LogOut size={14} />}
                          onClick={() => handleCheckOut(row.id)}
                          disabled={actionId === row.id || row.status !== "CHECKED_IN"}
                        >
                          Check Out
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal: Assign Host to Unassigned Request */}
        <Modal
          isOpen={Boolean(assignModalEntry)}
          onClose={() => setAssignModalEntry(null)}
          title="Assign Host Employee"
          footer={
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <Button variant="ghost" onClick={() => setAssignModalEntry(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAssignHostSubmit}
                disabled={!assignedUserId}
              >
                Confirm & Notify Host
              </Button>
            </div>
          }
        >
          {assignModalEntry && (
            <div>
              <p style={{ margin: "0 0 16px", fontSize: "0.85rem", color: "#64748b" }}>
                Assign visitor <strong>{assignModalEntry.visitor_name}</strong> to a team member in this department.
              </p>

              <FormField label="Select Host Employee" required>
                <SelectDropdown
                  name="assignedUserId"
                  value={assignedUserId}
                  onChange={(e) => setAssignedUserId(e.target.value)}
                  options={[
                    { label: "Choose staff member...", value: "" },
                    ...eligibleStaff.map((s) => ({
                      label: `${s.name} (${s.designation || "Staff"})`,
                      value: String(s.user_id),
                    })),
                  ]}
                  placeholder="Select employee..."
                />
              </FormField>
            </div>
          )}
        </Modal>

        {/* Modal: End of Day Auto Check-out Confirmation */}
        <Modal
          isOpen={showEodModal}
          onClose={() => setShowEodModal(false)}
          title="End of Day Auto Check-out"
          footer={
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <Button variant="ghost" onClick={() => setShowEodModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={eodLoading}
                onClick={handleAutoCheckoutEOD}
              >
                Confirm End of Day Check-out
              </Button>
            </div>
          }
        >
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <AlertTriangle size={24} style={{ color: "#d97706", flexShrink: 0 }} />
            <div>
              <p style={{ margin: "0 0 8px", fontSize: "0.95rem", fontWeight: 700, color: "#0f172a" }}>
                Check out all currently active visitors?
              </p>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", lineHeight: 1.4 }}>
                This will mark all visitors with active status <code>CHECKED_IN</code> across {currentSocietyName} as checked out for the closing of today&apos;s gate duty shift.
              </p>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
